import { APP_INITIALIZER, Injector, NgModule, inject } from '@angular/core';

import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';

import { FsAddressModule } from '@firestitch/address';
import { FS_ANALYTICS_CONFIG, FsAnalyticsModule } from '@firestitch/analytics';
import {
  FS_API_REQUEST_INTERCEPTOR,
  FsApi,
  FsApiModule,
} from '@firestitch/api';
import { FS_APP_ACL_CONFIG, FsAppAclModule } from '@firestitch/app-acl';
import { FS_ATTRIBUTE_CONFIG, FsAttributeModule } from '@firestitch/attribute';
import { FsAutocompleteChipsModule } from '@firestitch/autocomplete-chips';
import { FS_BANNER_CONFIG } from '@firestitch/banner';
import {
  BuildConfig,
  FS_BUILD_CONFIG,
  FsBuildService, UpdateAction,
} from '@firestitch/build';
import { FsCommonModule } from '@firestitch/common';
import { FsCookie } from '@firestitch/cookie';
import { FsDatePickerModule } from '@firestitch/datepicker';
import { FsDialogModule } from '@firestitch/dialog';
import { DRAWER_DEFAULT_CONFIG } from '@firestitch/drawer';
import { FsFieldEditorModule } from '@firestitch/field-editor';
import { FsFileModule } from '@firestitch/file';
import { ButtonStyle, FsFilterModule } from '@firestitch/filter';
import { FsFormModule } from '@firestitch/form';
import { FsGalleryModule } from '@firestitch/gallery';
import { FsBodyModule } from '@firestitch/html';
import { FsHtmlEditorConfig, FsHtmlEditorModule } from '@firestitch/html-editor';
import { FsListModule } from '@firestitch/list';
import { FS_MAP_GOOGLE_MAP_KEY } from '@firestitch/map';
import { FsMessage, FsMessageModule } from '@firestitch/message';
import { FsNavModule } from '@firestitch/nav';
import { FS_OFFLINE_CONFIG } from '@firestitch/offline';
import { FsPasswordModule } from '@firestitch/password';
import { FsPhoneModule } from '@firestitch/phone';
import { FsProgressModule } from '@firestitch/progress';
import { FsPromptModule } from '@firestitch/prompt';
import { FsScrollModule } from '@firestitch/scroll';
import { FsScrollbarModule } from '@firestitch/scrollbar';
import { FsSelectionModule } from '@firestitch/selection';
import { FsSigninModule, SigninConfig } from '@firestitch/signin';
import { FsSigninSecurityModule } from '@firestitch/signin-security';
import {
  FacebookSigninProvider, FsSocialSignin,
  GoogleSigninProvider, SocialSigninConfig,
} from '@firestitch/social-signin';
import { FsStore, FsStoreModule } from '@firestitch/store';
import { FsTabsModule } from '@firestitch/tabs';
import { FsTextEditorModule } from '@firestitch/text-editor';
import { FS_TRANSFER_HANDLER, FsTransferService } from '@firestitch/transfer';
import { FsUploadModule } from '@firestitch/upload';

import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { AclRoleLevels } from '@common/consts';
import { AttributeData, BannerData, OfflineData } from '@common/data';
import { environment } from '@env/environment';

import { AclRoleData } from '@app/admin/data';
import { BannerCollapseService } from '@libs/layouts/side-nav/services';


import {
  AclService,
  SessionService,
  SigninService,
} from './';
import { ListDefaultConfig } from './consts';
import { TransferHandler } from './handlers';
import { analyticsConfig, bannerConfig, offlineConfig, throwIfAlreadyLoaded } from './helpers';
import { attributeConfigFactory } from './helpers/attribute-config-factory';
import { ENVIRONMENT } from './injectors';
import { ApiInterceptorFactory } from './interceptors';
import { AppService } from './services';


// Froala activation key is populated from settings in the APP_INITIALIZER below.
// FS_HTML_EDITOR_CONFIG is built lazily on first editor use, after settings load,
// so mutating this object before then is sufficient — and avoids hardcoding the key.
const htmlEditorConfig: FsHtmlEditorConfig = { activationKey: '' };

@NgModule({
  imports: [
    FsAppAclModule,
    FsAnalyticsModule.forRoot(),
    FsSelectionModule,
    FsStoreModule,
    FsPasswordModule.forRoot(),
    FsGalleryModule.forRoot(),
    FsMessageModule.forRoot(),
    FsScrollbarModule.forRoot(),
    FsTabsModule.forRoot(),
    FsPromptModule.forRoot(),
    FsFormModule.forRoot(),
    FsFieldEditorModule.forRoot(),
    FsScrollModule.forRoot(),
    FsUploadModule.forRoot(),
    FsFileModule.forRoot(),
    FsAutocompleteChipsModule.forRoot(),
    FsAttributeModule.forRoot(),
    FsTextEditorModule.forRoot(),
    FsDatePickerModule.forRoot(),
    FsPhoneModule.forRoot(),
    FsDialogModule.forRoot(),
    FsCommonModule.forRoot(),
    FsAddressModule.forRoot(),
    FsBodyModule.forRoot(),
    FsNavModule.forRoot({ watchBrowserBackButton: false }),
    FsFilterModule.forRoot({ case: 'camel', queryParam: true, chips: true }),
    FsProgressModule.forRoot({ barColor: '#1FE3B9' }),
    FsSigninSecurityModule.forRoot({ case: 'camel' }),
    FsApiModule.forRoot({ maxFileConnections: 5 }),
    FsHtmlEditorModule.forRoot(htmlEditorConfig),
    FsSigninModule.forRoot({
      factory: (appService: AppService, signinService: SigninService): SigninConfig => {
        return {
          processSignin: (response, redirect) => signinService
            .processSignin(response, redirect),
          signinMeta: () => of({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
          showSocialSignins: true,
        };
      },
      deps: [AppService, SigninService],
    }),
    FsListModule.forRoot(ListDefaultConfig),
    FsFilterModule.forRoot({
      case: 'camel',
      buttonStyle: ButtonStyle.Flat,
      queryParam: true,
      chips: true,
      button: {
        label: '',
      },
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (
        appService: AppService,
        buildService: FsBuildService,
        socialSignin: FsSocialSignin,
        sessionService: SessionService,
      ) => {
        return () => appService.load()
          .pipe(
            switchMap(() => sessionService.refreshEnvironment()),
            tap(() => {
              buildService.listen();
            }),
            tap(() => {
              htmlEditorConfig.activationKey = appService.settings.htmlEditorActivationKey;

              const config: SocialSigninConfig = {
                providers: [
                  {
                    provider: new GoogleSigninProvider(appService.settings.googleClientId),
                    enabled: appService.settings.googleSigninEnabled,
                  },
                  {
                    provider: new FacebookSigninProvider(appService.settings.facebookAppId),
                    enabled: appService.settings.facebookSigninEnabled,
                  },
                ]
                  .filter((item) => item.enabled)
                  .map((item) => item.provider),
              };

              socialSignin.init(config);
            }),
          );
      },
      multi: true,
      deps: [AppService, FsBuildService, FsSocialSignin, SessionService],
    },
    {
      provide: FS_ANALYTICS_CONFIG,
      useFactory: analyticsConfig,
      deps: [Injector],
    },
    {
      provide: FS_MAP_GOOGLE_MAP_KEY,
      useFactory: (appService: AppService) => appService.settings.googleMapsKey,
      deps: [AppService],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'auto', appearance: 'outline' },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        maxWidth: '95vw',
        hasBackdrop: true,
        autoFocus: false,
        closeOnNavigation: true,
        minWidth: '450px',
      } as MatDialogConfig,
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '0ms',
        stretchTabs: false,
        dynamicHeight: false,
      } as MatTabsConfig,
    },
    {
      provide: FS_TRANSFER_HANDLER,
      useClass: TransferHandler,
      deps: [FsMessage, FsCookie],
    },
    {
      provide: FS_API_REQUEST_INTERCEPTOR,
      useFactory: ApiInterceptorFactory,
      deps: [Injector],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    // {
    //   provide: FS_SOCIAL_SIGNIN_CONFIG,
    //   useFactory: (appService: AppService): SocialSigninConfig => {
    //     const providers = [];

    //     if (appService.settings.googleClientId) {
    //       providers.push(new GoogleSigninProvider(appService.settings.googleClientId));
    //     }

    //     if (appService.settings.facebookAppId) {
    //       providers.push(new FacebookSigninProvider(appService.settings.facebookAppId));
    //     }

    //     return { providers };
    //   },
    //   deps: [AppService],
    // },
    {
      provide: FS_BUILD_CONFIG,
      useFactory: (): BuildConfig => {
        return {
          updateAction: environment.production ? UpdateAction.NavigationUpdate : null,
        };
      },
    },
    {
      provide: DRAWER_DEFAULT_CONFIG,
      useValue: {
        width: {
          side: {
            min: 300,
          },
        },
      },
    },
    {
      provide: FS_APP_ACL_CONFIG,
      useFactory: (aclRoleData: AclRoleData) => {
        return {
          permissions: aclRoleData.permissions(),
          levels: of(AclRoleLevels),
        };
      },
      deps: [AclRoleData],
    },
    {
      provide: FS_OFFLINE_CONFIG,
      useFactory: offlineConfig,
      deps: [OfflineData, AclService],
    },
    {
      provide: FS_BANNER_CONFIG,
      useFactory: bannerConfig,
      deps: [BannerData, BannerCollapseService],
    },
    FsTransferService,
    FsApi,
    FsStore,
    {
      provide: FS_ATTRIBUTE_CONFIG,
      useFactory: attributeConfigFactory,
      deps: [AttributeData, FsMessage],
    },
  ],
  exports: [
    FsProgressModule,
  ],
})
export class CoreModule {

  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

}
